import { useCallback, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { observer } from "mobx-react-lite";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { Button, Input, Modal } from "@synthesis/shared-ui";

import { AdminModalBody, AdminModalFooter } from "../../../components/admin-console/admin-modal";
import { type RoadFormValues, usePageStore } from "../constructor-roads-page.lib";

const roadSchema = z.object({
  name: z.string().trim().min(1, "Укажите наименование дороги"),
  code: z.string().trim().min(1, "Укажите код дороги"),
  shortName: z.string().trim().min(1, "Укажите краткое название"),
  administrationCode: z.string().trim().min(1, "Укажите код администрации"),
});

const EMPTY_ROAD: RoadFormValues = { name: "", code: "", shortName: "", administrationCode: "" };

/** Общие поля формы дороги: наименование, код, сокращение, код администрации. */
const RoadFormFields = () => (
  <div className="flex flex-col gap-4">
    <Input name="name" label="Наименование" required placeholder="Наименование дороги" />
    <div className="grid grid-cols-2 gap-4">
      <Input name="code" label="Код дороги" required placeholder="Введите код" />
      <Input name="shortName" label="Краткое название" required placeholder="Введите сокращение" />
      <Input name="administrationCode" label="Код администрации" required placeholder="Введите код" />
    </div>
  </div>
);

/** Модалка «Новая железная дорога» / «Изменение железной дороги». Открывается через стор. Бэка нет — сохранение только закрывает окно. */
export const RoadFormModal = observer(() => {
  const store = usePageStore()!;
  const action = store.popupAction;
  const isOpen = action !== null;
  const isEdit = action?.type === "edit";
  const road = action?.type === "edit" ? action.road : undefined;

  const defaults = useMemo<RoadFormValues>(
    () => (road ? { name: road.name, code: road.code, shortName: "", administrationCode: "" } : EMPTY_ROAD),
    [road],
  );
  const methods = useForm<RoadFormValues>({ resolver: zodResolver(roadSchema), mode: "onChange", defaultValues: defaults });

  // При каждом открытии — префилл формы (для «Изменения дороги» данные выбранной дороги).
  useEffect(() => {
    if (isOpen) {
      methods.reset(defaults);
    }
  }, [defaults, isOpen, methods]);

  const handleClose = useCallback(() => {
    methods.reset(EMPTY_ROAD);
    store.setPopupAction(null);
  }, [methods, store]);

  return (
    <Modal.Root isOpen={isOpen} setIsOpen={(open) => (open ? null : handleClose())}>
      <Modal.Content className="w-[43.5rem] max-w-[90vw]">
        <Modal.Title>{isEdit ? "Изменение железной дороги" : "Новая железная дорога"}</Modal.Title>
        <FormProvider {...methods}>
          <AdminModalBody>
            <h4 className="heading-h4 text-type-common">{isEdit ? "Изменение железной дороги" : "Новая железная дорога"}</h4>
            <RoadFormFields />
          </AdminModalBody>

          <AdminModalFooter className="justify-between">
            <div className="flex items-center gap-4">
              {/* TODO(FRONT-1390): сохранение/удаление дороги — после появления бэкенда. */}
              <Button theme="primary" size="lg" onClick={methods.handleSubmit(handleClose)}>
                {isEdit ? "Сохранить" : "Добавить"}
              </Button>
              <Button theme="secondary" size="lg" onClick={handleClose}>
                Отмена
              </Button>
            </div>
            {isEdit ? (
              <Button theme="secondary" size="lg" onClick={handleClose}>
                Удалить
              </Button>
            ) : null}
          </AdminModalFooter>
        </FormProvider>
      </Modal.Content>
    </Modal.Root>
  );
});
